import { ReactElement, useEffect, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useSelector } from 'react-redux'
import Box from '@mui/material/Box'
import { TextField } from '@mui/material'
import { validateSiloSchema } from 'utils'
import { Modal } from 'components'
import { RootState } from 'store'
import { ICreatSilo, ISilo } from 'types/models'
import { yupResolver } from '@hookform/resolvers/yup'

interface IEditSiloModalProps {
  onConfirm(data: ISilo): void
  initialData?: ICreatSilo
  mode?: 'add' | 'edit'
}

const useEditSiloModal = ({
  onConfirm,
  initialData,
  mode = 'add',
}: IEditSiloModalProps): [ReactElement, VoidFunction] => {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const toggleModal = () => setIsOpen(!isOpen)
  const companyId = useSelector(
    (state: RootState) => state.user.profile?.company?.id
  )

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: initialData?.name || '',
      company_id: companyId,
    },
    resolver: yupResolver(validateSiloSchema),
  })

  const resetRef = useRef<Partial<ISilo>>(initialData)

  useEffect(() => {
    if (JSON.stringify(resetRef.current) !== JSON.stringify(initialData)) {
      if (initialData) {
        reset(initialData)
        resetRef.current = initialData
      }
    }
  }, [initialData, reset])

  const onSubmit = (data: ISilo) => {
    onConfirm(data)
    toggleModal()
    reset()
  }

  return [
    <Modal
      isOpen={isOpen}
      onClose={toggleModal}
      title={mode === 'add' ? 'Add Silo' : 'Edit Silo'}
      onConfirm={handleSubmit(onSubmit)}
      onConfirmButtonText="Ok"
    >
      <Box px="30px" py="20px">
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={mode === 'edit' ? initialData?.name || '' : 'Silo Name'}
              variant="outlined"
              error={!!error}
              helperText={error ? error.message : ''}
            />
          )}
        />
      </Box>
    </Modal>,
    toggleModal,
  ]
}

export default useEditSiloModal
